import { validatePasswordPolicy } from '@pulsify/shared/password'
import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Check, Loader2, Mail } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { authApi } from '~/entities/session/api/auth'
import { useRegister } from '~/entities/session/model/use-auth'
import { ConsentFields, EmailField } from '~/features/auth/auth-form-fields'
import { AuthHeading, AuthShell, authLinkClassName } from '~/features/auth/auth-shell'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { log } from '~/shared/lib/logger'
import { registerSchema } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Label } from '~/shared/ui/label'
import { PasswordInput } from '~/shared/ui/password-input'
import { PasswordStrength } from '~/shared/ui/password-strength'

export const Route = createFileRoute('/register')({
  component: RegisterPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      email: (search.email as string) || '',
    }
  },
})

function RegisterPage() {
  const register = useRegister()
  const { email: searchEmail } = Route.useSearch()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [focusedField, setFocusedField] = useState<string | null>(null)

  const form = useForm({
    defaultValues: {
      email: searchEmail || '',
      password: '',
      acceptPrivacyPolicy: false,
      acceptTerms: false,
    },
    onSubmit: ({ value }) => {
      register.mutate(
        { email: value.email, password: value.password },
        {
          onSuccess: (result) => {
            if (!result.upgraded) setSentTo(value.email)
          },
        },
      )
    },
  })

  const isValid = (values: typeof form.state.values) =>
    registerSchema.safeParse(values).success &&
    validatePasswordPolicy(values.password, [values.email]).success

  if (sentTo) {
    return (
      <AuthShell>
        <VerifyEmail email={sentTo} />
      </AuthShell>
    )
  }

  return (
    <AuthShell
      footer={
        <p>
          {m.alreadyHaveAnAccount()}{' '}
          <Link to="/login" className={authLinkClassName}>
            {m.signIn()}
          </Link>
        </p>
      }
    >
      <AuthHeading title={m.createAccount()} description={m.registerSubtitle()} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (isValid(form.state.values)) form.handleSubmit()
        }}
        className="space-y-4"
      >
        <EmailField form={form} focusedField={focusedField} setFocusedField={setFocusedField} />

        <form.Field
          name="password"
          validators={createFieldValidators(registerSchema.shape.password)}
        >
          {(field) => {
            const isFocused = focusedField === field.name
            const showError =
              field.state.meta.isTouched && field.state.meta.errors.length > 0 && !isFocused
            return (
              <div className="space-y-2">
                <Label htmlFor={field.name}>{m.password()}</Label>
                <PasswordInput
                  id={field.name}
                  autoComplete="new-password"
                  placeholder={m.passwordPlaceholder()}
                  value={field.state.value}
                  onFocus={() => setFocusedField(field.name)}
                  onBlur={() => {
                    setFocusedField(null)
                    field.handleBlur()
                  }}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={showError ? 'border-destructive focus-visible:ring-destructive' : ''}
                />
                {showError && (
                  <p className="text-destructive text-sm">{field.state.meta.errors[0]}</p>
                )}
                <PasswordStrength
                  password={field.state.value}
                  userInputs={[form.getFieldValue('email')]}
                />
              </div>
            )
          }}
        </form.Field>

        <div className="pt-1">
          <ConsentFields form={form} />
        </div>

        {register.isError && (
          <div className="squircle rounded-lg border border-destructive/20 bg-destructive/10 p-3">
            <p className="text-destructive text-sm">{getErrorMessageFromError(register.error)}</p>
          </div>
        )}

        <form.Subscribe selector={(state) => state.values}>
          {(values) => (
            <Button
              type="submit"
              variant="brand"
              size="lg"
              className="w-full"
              disabled={!isValid(values) || register.isPending}
            >
              {register.isPending ? (
                <>
                  <Loader2 className="animate-spin" />
                  {m.creating()}
                </>
              ) : (
                m.createAccount()
              )}
            </Button>
          )}
        </form.Subscribe>
      </form>
    </AuthShell>
  )
}

function VerifyEmail({ email }: { email: string }) {
  const navigate = useNavigate()
  const [verified, setVerified] = useState(false)
  const verifiedRef = useRef(false)

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const status = await authApi.checkVerificationStatus()
        if (status.isVerified && !verifiedRef.current) {
          verifiedRef.current = true
          setVerified(true)
          clearInterval(interval)
          setTimeout(() => {
            navigate({ to: '/dashboard' })
          }, 2000)
        }
      } catch (error) {
        log.error(error as Error, 'Failed to check verification status')
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [navigate])

  const resend = async () => {
    try {
      await authApi.resendVerification(email)
    } catch (error) {
      log.error(error as Error, 'Failed to resend verification')
      toast.error(getErrorMessageFromError(error))
    }
  }

  if (verified) {
    return (
      <AuthHeading
        icon={<Check />}
        title={m.emailVerified()}
        description={m.yourAccountHasBeenSuccessfullyCreatedYouWillBeRedirected()}
      />
    )
  }

  return (
    <>
      <AuthHeading
        icon={<Mail />}
        title={m.verifyEmail()}
        description={
          <>
            {m.weHaveSentAVerificationEmailTo()}{' '}
            <span className="font-medium text-foreground">{email}</span>.{' '}
            {m.pleaseCheckYourInboxAndClickOnTheVerificationLink()}
          </>
        }
      />
      <p role="status" className="flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="size-4 shrink-0 animate-spin motion-reduce:animate-none" />
        {m.emailVerificationPending()}
      </p>
      <Button type="button" variant="outline" onClick={resend} className="w-full">
        {m.resendEmail()}
      </Button>
    </>
  )
}
